import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDownIcon, ChevronLeftIcon, LinkIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../../common/New-ToolTip";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import { Listbox, Transition } from "@headlessui/react";
import InterventionCreateNote from "../../../../common/tooltip/medication-review/intervention-create";
import MedicineSelect from "../../services/select/medication-select";
import {
  isFieldError,
  isValidVigilanceSelection,
} from "../../../../../helpers/input-validations";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import Draggable from "react-draggable";
import { AdherenceTypes, CommonQuestionValueTypes } from "./data";
import { DPTOptions } from "../../../text/data";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createOrUpdateIntervention, searchInterventions } from "../../../../../redux/modules/intervention/actions";
import { fetchClinicalServices } from "../../../../../redux/modules/clinical-services/actions";
import { getMedicationReview } from "../../../../../redux/modules/medical-summary/actions";
import {
  getEligibleMedicationReviewsForInterventionLink,
  getMedicationReviewRecordDate,
  personalMedicationReviewTypeLabel,
} from "../../../../../helpers/medication-review-pmr-link";
import { ClinicalServiceTypes } from "../../../text/data";
import { InterventionStatus } from "../../../Patient-Menu/Interventions/data";
import { createPortal } from 'react-dom';
import { formatDateToDD_MMM_YYYY } from "../../../../../helpers/functions";
import { drugTypes, medicationTypeOptions } from "../clinical-assessment/data";

const LINKABLE_INTERVENTION_PRESCRIPTION_TYPES = [
  drugTypes.Prescription,
  drugTypes.Additional,
];

// Same filter + sort as medications.jsx so prescription-link options match the sidebar lists.
function getSidebarAlignedMedications(medications, drugStatus, sortBy) {
  const meds = (Array.isArray(medications) ? medications : []).filter(
    (x) => drugStatus === "all" || x.status === drugStatus
  );
  const bySort = {
    alphabetic: (a, b) =>
      String(a.displayName || "").localeCompare(String(b.displayName || "")),
    newest: (a, b) => new Date(b.created) - new Date(a.created),
    oldest: (a, b) => new Date(a.created) - new Date(b.created),
  };
  const cmp = bySort[sortBy];
  return cmp ? [...meds].sort(cmp) : meds;
}

function mapMedicationReviewItemToMrLinkOption(item) {
  const raw = getMedicationReviewRecordDate(item);
  const dateLabel =
    raw && !Number.isNaN(new Date(raw).getTime())
      ? formatDateToDD_MMM_YYYY(new Date(raw))
      : "N/A";
  return {
    value: item.id,
    label: `${personalMedicationReviewTypeLabel(item.medicationReviewTypeCode)} | ${dateLabel}`,
  };
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** API intervention type enum values */
const interventionTypes = {
  Pharmaceutical: "PharmaceuticalOpinion",
  Prescription: "prescriptionAdaptation",
};

/** Which top-level card (Medication Intervention / Clinical Intervention) is active. Independent of interventionType, which only drives the DTP dropdown + is sent as the API's interventionType. */
const interventionCategories = {
  Medication: "medication",
  Clinical: "clinical",
};

const DOES_NOT_APPLY_ACTION_PLAN_VALUE = "Does not apply";

const actionPlanOptions = [
  { label: "Adapt Prescription" },
  { label: "Change/ Start/ Stop" },
  { label: "Contact Prescriber" },
  { label: "Eliminate Patient Barriers" },
  { label: "Give Immunization" },
  { label: "Initiate Drug - Disease Monitoring" },
  { label: "Initiate Drug Therapy Monitoring" },
  { label: "Initiate Monitoring for Medical Condition" },
  { label: "Provide Education" },
  { label: "Referral to Other" },
  { label: "Referral to Physician" },
  { label: "Does not apply" },
];

const AdaptationOptions = [
  { value: "ChangeDose", label: "Change Dose" },
  { value: "ChangeForm", label: "Change Dose Form" },
  { value: "ChangeRegimen", label: "Change The Regimen" },
  { value: "ChangeRoute", label: "Change The Route Of Administration" },
];

const URGENCY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

/** Hard ceiling so the modal (sized once to fit its content on open) never exceeds this. */
const MODAL_MAX_SIZE = [1400, 1000];

const PILL_TOGGLE_CLASS =
  "disabled:opacity-40 m-2 h-4 w-auto rounded-full inline-flex border-REELTeal border text-teal-300 hover:text-teal-300 hover:bg-gray-20 transition-all duration-300 items-center font-semibold justify-center px-3 py-4";

function actionPlanFieldId(label) {
  return `action-plan-${String(label).replace(/[^a-zA-Z0-9]+/g, "-")}`;
}

/** Existing medications carry their own values; new ones fall back to `inv` (the pending intervention), then the selected medicine. */
function pickMedicationDetail(hasExistingMedication, invValue, existingValue, selectedValue) {
  if (hasExistingMedication) return invValue || existingValue || null;
  return selectedValue || null;
}

function pickExistingOrSelected(hasExistingMedication, existingValue, selectedValue) {
  return hasExistingMedication ? existingValue : selectedValue || null;
}

function buildPersonalMrLinkFields(interventionType, linkedMedicationReviewClinicalServiceId) {
  const includePersonalMrLink =
    interventionType === interventionTypes.Pharmaceutical &&
    Boolean(linkedMedicationReviewClinicalServiceId);
  return {
    addToPersonalMedicationReview: includePersonalMrLink,
    linkedMedicationReviewClinicalServiceId: includePersonalMrLink
      ? linkedMedicationReviewClinicalServiceId
      : null,
  };
}

function buildMedicationIdentityFields({
  hasExistingMedication,
  existingMedication,
  selectedMedicine,
  medicationType,
  inv,
}) {
  return {
    medicationId: hasExistingMedication ? existingMedication.id : EMPTY_GUID,
    medicationType: hasExistingMedication
      ? existingMedication?.medicationType
      : medicationType || null,
    medicationName: pickMedicationDetail(
      hasExistingMedication,
      inv?.medicationName,
      existingMedication?.displayName || existingMedication?.name,
      selectedMedicine?.displayText || selectedMedicine?.label
    ),
    dosage: pickMedicationDetail(
      hasExistingMedication,
      inv?.dosage,
      existingMedication?.dosage || existingMedication?.dose,
      selectedMedicine?.dose
    ),
    form: pickMedicationDetail(
      hasExistingMedication,
      inv?.form,
      existingMedication?.form,
      selectedMedicine?.form
    ),
    frequency: pickMedicationDetail(
      hasExistingMedication,
      inv?.frequency,
      existingMedication?.frequency,
      selectedMedicine?.frequency
    ),
    route: pickMedicationDetail(
      hasExistingMedication,
      inv?.route,
      existingMedication?.route,
      selectedMedicine?.route
    ),
    medicationGenCode: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.medicationGenCode,
      selectedMedicine?.identifier || selectedMedicine?.value
    ),
    medicationGenCodeType: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.medicationGenCodeType,
      selectedMedicine?.type
    ),
    din: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.din,
      selectedMedicine?.din
    ),
    manufacturerCode: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.manufacturerCode,
      selectedMedicine?.manufacturerCode
    ),
    manufacturerName: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.manufacturerName,
      selectedMedicine?.manufacturerName
    ),
    usualQuantity: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.usualQuantity,
      selectedMedicine?.usualQuantity
    ),
    usualDuration: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.usualDuration,
      selectedMedicine?.usualDuration
    ),
    dispenseQuantity: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.dispenseQuantity,
      selectedMedicine?.dispenseQuantity
    ),
    dispenseUnit: pickExistingOrSelected(
      hasExistingMedication,
      existingMedication?.dispenseUnit,
      selectedMedicine?.dispenseUnit
    ),
  };
}

function buildInterventionAssessmentDetails({
  hasExistingMedication,
  existingMedication,
  selectedMedication,
  selectedMedicine,
  medicationType,
  comments,
  DTPValue,
  actionPlan,
  urgency,
  interventionType,
  linkedMedicationReviewClinicalServiceId,
}) {
  const inv = selectedMedication?.intervention;
  return {
    ...buildMedicationIdentityFields({
      hasExistingMedication,
      existingMedication,
      selectedMedicine,
      medicationType,
      inv,
    }),
    whoChangedMedication: inv?.whoChangedMedication ?? null,
    whyChangedMedication: inv?.whyChangedMedication ?? null,
    whenChangedMedication: inv?.whenChangedMedication ?? null,
    otherChangeReason: inv?.otherChangeReason ?? null,
    pharmacistComments: comments ?? inv?.pharmacistComments ?? null,
    reason: inv?.reason ?? null,
    reasonGenCode: inv?.reasonGenCode ?? null,
    reasonComments: inv?.reasonComments ?? null,
    whenChangedDate: inv?.whenChangedDate ?? null,
    override: inv?.override ?? null,
    overrideReason: inv?.overrideReason ?? null,
    comments,
    prn: inv?.prn ?? null,
    prnReason: inv?.prnReason ?? null,
    sig: inv?.sig ?? null,
    drugTherapyProblem: DTPValue,
    actionPlan,
    urgencyLevel: urgency,
    ...buildPersonalMrLinkFields(interventionType, linkedMedicationReviewClinicalServiceId),
  };
}

function buildInterventionDraftPayload({
  interventionType,
  DTPValue,
  actionPlan,
  urgency,
  comments,
  linkedMedicationReviewClinicalServiceId,
}) {
  return {
    interventionType,
    drugTherapyProblem: DTPValue,
    actionPlan,
    urgencyLevel: urgency,
    pharmacistComments: comments,
    ...buildPersonalMrLinkFields(interventionType, linkedMedicationReviewClinicalServiceId),
  };
}

const InterventionCreate = ({
  open,
  selectedMedication = null,
  onClose = () => { },
  onSave = () => { },
  goBack = () => { },
  onSkip = () => {},
  onCross = () => {},
}) => {
  const dispatch = useDispatch();
  const { id: patientId, serviceId } = useParams();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const scrollRef = useRef(null);
  // Fixed side margin so the modal fits the viewport when it opens.
  // The modal isn't manually resizable, so this only runs once per open.
  const MARGIN = 20;
  const VIEWPORT_BUFFER = 80;
  const [dimensions, setDimensions] = useState({ width: 800, height: 870 });
  const { clinicalServiceObj, clinicalServiceName, medications = [], clinicalServicesSmall } = useSelector(
    (state) => state.clinicalService
  );
  const { drugStatus, sortBy } = useSelector((state) => state.medications);
  const medicationReviewResponse = useSelector(
    (state) => state.medicalSummary?.medicationReviewResponse ?? []
  );
  const interventionsFromStore = useSelector((state) => state.intervention?.interventions ?? []);
  const [clicked, setClicked] = useState(false);

  const [selectedMedicine, setSelectedMedicine] = useState({
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
  });
  const [medicationNameInputMode, setMedicationNameInputMode] = useState(false);
  const [linkedPrescriptionId, setLinkedPrescriptionId] = useState(null);
  const [selectedMedicineError, setSelectedMedicineError] = useState(false);
  const [openUncodedDrug, setOpenUncodedDrug] = useState(false);
  const {
    linkToCurrentPrescriptionOptions,
    prescriptionLinkGroups,
    selectedLinkPrescriptionOption,
    linkedPrescription,
  } = useMemo(() => {
    const meds = medications || [];
    const sidebarMeds = getSidebarAlignedMedications(meds, drugStatus, sortBy);
    const eligible = sidebarMeds.filter((med) =>
      LINKABLE_INTERVENTION_PRESCRIPTION_TYPES.includes(med?.medicationType)
    );
    const toOption = (med) => ({
      label: med.displayName ?? "",
      value: med.id,
    });
    const prescriptionItems = eligible
      .filter((m) => m.medicationType === drugTypes.Prescription)
      .map(toOption);
    const additionalItems = eligible
      .filter((m) => m.medicationType === drugTypes.Additional)
      .map(toOption);

    const prescriptionLinkGroups = [
      ...(prescriptionItems.length > 0
        ? [{ heading: "Prescriptions", items: prescriptionItems }]
        : []),
      ...(additionalItems.length > 0
        ? [{ heading: "Additional Medication", items: additionalItems }]
        : []),
    ];

    const linkToCurrentPrescriptionOptions =
      prescriptionLinkGroups.flatMap((g) => g.items);

    const selectedLinkPrescriptionOption =
      linkedPrescriptionId == null
        ? null
        : linkToCurrentPrescriptionOptions.find(
            (o) => String(o.value) === String(linkedPrescriptionId)
          ) ?? null;

    const linkedPrescription =
      linkedPrescriptionId == null
        ? null
        : meds.find(
            (med) => String(med?.id) === String(linkedPrescriptionId)
          ) || null;

    return {
      linkToCurrentPrescriptionOptions,
      prescriptionLinkGroups,
      selectedLinkPrescriptionOption,
      linkedPrescription,
    };
  }, [medications, linkedPrescriptionId, drugStatus, sortBy]);

  useEffect(() => {
    if (linkedPrescriptionId == null) return;
    const stillValid = linkToCurrentPrescriptionOptions.some(
      (o) => String(o.value) === String(linkedPrescriptionId)
    );
    if (!stillValid) setLinkedPrescriptionId(null);
  }, [linkToCurrentPrescriptionOptions, linkedPrescriptionId]);

  const isLinkedToCurrentPrescription = Boolean(linkedPrescriptionId);

  const [interventionType, setInterventionType] = useState(
    interventionTypes.Pharmaceutical
  );
  /** Top card selection (Medication Intervention / Clinical Intervention) — separate from interventionType. */
  const [interventionCategory, setInterventionCategory] = useState(
    interventionCategories.Medication
  );
  const [DTPValue, setDTPValue] = useState(null);
  const [actionPlan, setActionPlan] = useState([]);
  const [other, setOther] = useState(false);
  const [urgency, setUrgency] = useState(null);
  const [linkedMedicationReviewClinicalServiceId, setLinkedMedicationReviewClinicalServiceId] =
    useState(null);
  const [comments, setComments] = useState(null);
  /** Which of the "Link to Current Prescription" / "Add Medication" panels is active; null = neither chosen yet. */
  const [activeMedicationEntry, setActiveMedicationEntry] = useState(null);
  const [medicationType, setMedicationType] = useState(null);
  const [medicationTypeError, setMedicationTypeError] = useState(false);
  const [DTPError, setDTPError] = useState(false);
  const [urgencyError, setUrgencyError] = useState(false);
  const [interventionTypeError, setInterventionTypeError] = useState(false);
  const [id, setId] = useState(null);
  const isMedicationReviewService =
    clinicalServiceName === ClinicalServiceTypes.MedicationReview;

  const personalMrLinkOptions = useMemo(() => {
    const sorted = getEligibleMedicationReviewsForInterventionLink({
      clinicalServicesSmall,
      medicationReviewResponse,
      interventionsFromStore,
      currentServiceId: serviceId,
    });
    return sorted.map(mapMedicationReviewItemToMrLinkOption);
  }, [
    clinicalServicesSmall,
    medicationReviewResponse,
    interventionsFromStore,
    serviceId,
  ]);

  useEffect(() => {
    if (!open || !patientId) return;
    dispatch(
      getMedicationReview(patientId, {
        medicationReviewTypeCode: null,
        date: null,
      })
    );
    dispatch(fetchClinicalServices(patientId));
    dispatch(
      searchInterventions(patientId, {
        pageNumber: 1,
        pageSize: 200,
      })
    );
  }, [open, patientId, dispatch]);

  useEffect(() => {
    if (linkedMedicationReviewClinicalServiceId == null) return;
    const stillValid = personalMrLinkOptions.some(
      (o) => String(o.value) === String(linkedMedicationReviewClinicalServiceId)
    );
    if (!stillValid) setLinkedMedicationReviewClinicalServiceId(null);
  }, [personalMrLinkOptions, linkedMedicationReviewClinicalServiceId]);

  useEffect(() => {
    if (interventionType !== interventionTypes.Pharmaceutical) return;
    if (personalMrLinkOptions.length !== 1) return;
    const onlyId = personalMrLinkOptions[0].value;
    if (String(linkedMedicationReviewClinicalServiceId || "") !== String(onlyId)) {
      setLinkedMedicationReviewClinicalServiceId(onlyId);
    }
  }, [
    interventionType,
    personalMrLinkOptions,
    linkedMedicationReviewClinicalServiceId,
  ]);

  useEffect(() => {
    if (selectedMedication) {
      let interventionObj = selectedMedication.intervention;
      setId(selectedMedication.interventionId);
      setInterventionType(
        interventionObj && interventionObj.interventionType
          ? interventionObj.interventionType
          : interventionTypes.Pharmaceutical
      );
      setDTPValue(
        interventionObj && interventionObj.drugTherapyProblem
          ? interventionObj.drugTherapyProblem
          : getDefaultDTPValue()
      );
      setActionPlan(
        interventionObj && interventionObj.actionPlan
          ? interventionObj.actionPlan
          : []
      );
      setUrgency(
        interventionObj && interventionObj.urgencyLevel
          ? interventionObj.urgencyLevel
          : null
      );
      setComments(
        interventionObj && interventionObj.pharmacistComments
          ? interventionObj.pharmacistComments
          : null
      );
      const invDetails =
        selectedMedication?.interventionAssessment?.interventionAssessmentForm
          ?.interventionDetails;
      const existingMrLink =
        invDetails?.linkedMedicationReviewClinicalServiceId ??
        interventionObj?.linkedMedicationReviewClinicalServiceId ??
        null;
      setLinkedMedicationReviewClinicalServiceId(
        existingMrLink != null && existingMrLink !== ""
          ? String(existingMrLink)
          : null
      );
    } else {
      setLinkedMedicationReviewClinicalServiceId(null);
    }
  }, [selectedMedication, open]);

  useEffect(() => {
    if (!open || selectedMedication !== null) return;
    setInterventionCategory(
      isMedicationReviewService
        ? interventionCategories.Medication
        : interventionCategories.Clinical
    );
    // Prescription Adaptation is disabled for now (its DTP list isn't defined yet),
    // so Pharmaceutical Opinion is the only selectable Intervention Type.
    setInterventionType(interventionTypes.Pharmaceutical);
    // Neither toggle button should appear pre-selected on a fresh open, even if a
    // prior open in this same mounted instance left one active.
    setActiveMedicationEntry(null);
    // API enum map lives at module scope
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset category when opening create flow only
  }, [open, selectedMedication, isMedicationReviewService]);

  const getDefaultDTPValue = () => {
    if (selectedMedication.consumption !== AdherenceTypes.AsPrescribed)
      return "nonCompliance";
    else if (
      selectedMedication.working === CommonQuestionValueTypes.No &&
      selectedMedication.safe === CommonQuestionValueTypes.Yes
    )
      return null;
    else if (selectedMedication.working === CommonQuestionValueTypes.No)
      return "subOptimalResponse";
    else if (selectedMedication.safe === CommonQuestionValueTypes.Yes)
      return "adverseDrugReaction";
    else return "nonCompliance";
  };

  const handleChange = (e) => {
    if (e.target.checked) {
      if (e.target.name === DOES_NOT_APPLY_ACTION_PLAN_VALUE) {
        setActionPlan([DOES_NOT_APPLY_ACTION_PLAN_VALUE]);
        setOther(false);
        return;
      }

      setActionPlan([
        ...actionPlan.filter(
          (item) => item !== DOES_NOT_APPLY_ACTION_PLAN_VALUE
        ),
        e.target.name,
      ]);
    } else {
      setActionPlan(actionPlan.filter((item) => item !== e.target.name));
    }
  };

  const vigilanceFieldsToCheck = [
    {
      field: selectedMedicine,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedMedicineError,
      name: "selectedMedicine",
    },
    {
      field: medicationType,
      checkFn: (v) => Boolean(v),
      setErrorFn: setMedicationTypeError,
      name: "medicationType",
    },
  ];

  const fieldsToCheck = [
    {
      field: DTPValue,
      checkFn: isFieldError,
      setErrorFn: setDTPError,
      alter: false,
    },
    {
      field: urgency,
      checkFn: isFieldError,
      setErrorFn: setUrgencyError,
      alter: false,
    },
    {
      field: interventionType,
      checkFn: isFieldError,
      setErrorFn: setInterventionTypeError,
      alter: false,
    },
  ];

  const resetForm = () => {
    setSelectedMedicine(null);
    setLinkedPrescriptionId(null);
    setActiveMedicationEntry(null);
    setMedicationType(null);
    setMedicationTypeError(false);
    setComments("");
    setDTPValue("nonCompliance");
    setActionPlan([]);
    setOther(false);
    setUrgency(null);
    setLinkedMedicationReviewClinicalServiceId(null);
    setInterventionCategory(
      clinicalServiceName === ClinicalServiceTypes.MedicationReview
        ? interventionCategories.Medication
        : interventionCategories.Clinical
    );
    setInterventionType(interventionTypes.Pharmaceutical);
    setClicked(false);
  };

  const onSaveClick = async () => {
    try {
      // Validate required data
      if (!patientId) {
        console.error("Patient ID is required");
        return null;
      }

      // Determine if we have an existing medication or need to create new medication details
      const existingMedication =
        selectedMedication?.id
          ? selectedMedication
          : linkedPrescription?.id
          ? linkedPrescription
          : null;
      const hasExistingMedication = Boolean(existingMedication?.id);
      const hasNewMedication = isValidVigilanceSelection(selectedMedicine);

      // Medication selection is only required for the Medication Intervention
      // workflow; Clinical Intervention has no medication fields to fill in.
      if (
        interventionCategory === interventionCategories.Medication &&
        !hasExistingMedication &&
        !hasNewMedication
      ) {
        console.error(
          "Either existing medication or new medication selection is required"
        );
        return null;
      }

      const interventionDetails = buildInterventionAssessmentDetails({
        hasExistingMedication,
        existingMedication,
        selectedMedication,
        selectedMedicine,
        medicationType,
        comments,
        DTPValue,
        actionPlan,
        urgency,
        interventionType,
        linkedMedicationReviewClinicalServiceId,
      });

      // Prepare intervention data for API
      const interventionData = {
        id:
          selectedMedication?.interventionId ||
          EMPTY_GUID,
        patientId: patientId,
        // Only include medicationId if we have an existing medication
        // For new medications, omit it and let the backend handle it
        ...(hasExistingMedication && { medicationId: existingMedication.id }),
        interventionType: interventionType, // Use component variable
        status: InterventionStatus.Active,
        clinicalServiceId: serviceId, // Use serviceId from URL params
        clinicalServiceType: serviceId
          ? clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
            ? "Minor Ailment"
            : "Medication Review"
          : null, // Use clinical service name from Redux
        clinicalServiceTypeCode: serviceId
          ? clinicalServiceObj?.minorAilmentTypeCode ||
          clinicalServiceObj?.medicationReviewTypeCode
          : null, // Use clinical service name as code
        interventionAssessment: {
          interventionAssessmentForm: {
            interventionDetails,
            currentStep: 1,
            currentSubStep: 1,
            startDateTime: new Date().toISOString(),
            endDateTime: null,
          },
        },
      };

      // Call the API and get the response
      const responseData = await dispatch(
        createOrUpdateIntervention(patientId, interventionData)
      );

      // Extract the intervention ID from the response
      const interventionId = responseData?.id;

      // Return the intervention ID
      return interventionId;
    } catch (error) {
      // Keep dialog open on error - error handling is done in the action
      console.error("Failed to save intervention:", error);
      return null;
    }
  };

  const saveIntervention = async () => {
    setIsSaving(true);
    setClicked(true);
    let allFieldsValid = true;

    if (
      interventionCategory === interventionCategories.Medication &&
      selectedMedication === null &&
      !isLinkedToCurrentPrescription
    ) {
      let vigilanceValid = true;
      vigilanceFieldsToCheck.forEach(({ field, checkFn, setErrorFn }) => {
        const hasError = !checkFn(field);
        setErrorFn(hasError);
        if (hasError) {
          vigilanceValid = false;
          allFieldsValid = false;
        }
      });
      // Surface the errors: if the Add Medication panel isn't open, the fields
      // failing validation aren't visible, so reveal that panel.
      if (!vigilanceValid) setActiveMedicationEntry("add");
    }

    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = alter ? !checkFn(field) : checkFn(field);
      setErrorFn(hasError);
      if (hasError) {
        allFieldsValid = false;
      }
    });

    if (!allFieldsValid) {
      setIsSaving(false);
      return false;
    }

    const interventionObj = buildInterventionDraftPayload({
      interventionType,
      DTPValue,
      actionPlan,
      urgency,
      comments,
      linkedMedicationReviewClinicalServiceId,
    });

    const interventionId = await onSaveClick();
    if (!interventionId) {
      setIsSaving(false);
      return false;
    }

    // Always notify parent (medication card merges draft; PMR / other views may ignore extra args).
    onSave(interventionObj, interventionId);

    setIsSaving(false);
    return true;
  };

  const manageStep = async () => {
    const isSaved = await saveIntervention();
    if (isSaved) {
      onClose(false);
      resetForm();
    }
  };

  const handleCompleteNow = async () => {
    const isSaved = await saveIntervention();
    if (!isSaved) {
      return;
    }

    onSkip();
    onClose(false);
    resetForm();
    if (patientId) {
      navigate(`/patient/${patientId}/interventions`);
    }
  };

  const moveBack = () => {
    const interventionObj = buildInterventionDraftPayload({
      interventionType,
      DTPValue,
      actionPlan,
      urgency,
      comments,
      linkedMedicationReviewClinicalServiceId,
    });

    goBack(interventionObj, id);
    resetForm();
  };

  useEffect(() => {
    if (!clicked) return;
    setDTPError(isFieldError(DTPValue));
    setUrgencyError(isFieldError(urgency));
    setInterventionTypeError(isFieldError(interventionType));
  }, [clicked, DTPValue, urgency, interventionType]);

  const [container, setContainer] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = document.getElementById('main-content-area');
    if (el) setContainer(el);
  }, []);

  const scrollToBottom = (behavior = 'smooth') => {
    try {
      if (scrollRef.current && typeof scrollRef.current.scrollTo === 'function') {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior });
      }
    } catch (err) {
      void err;
    }
  };

  useEffect(() => {
    if (!actionPlan || actionPlan.length === 0) return;
    const t = setTimeout(() => scrollToBottom(), 60);
    return () => clearTimeout(t);
  }, [actionPlan.length]);

  useEffect(() => {
    if (!urgency) return;
    const t = setTimeout(() => scrollToBottom(), 60);
    return () => clearTimeout(t);
  }, [urgency]);

    useEffect(() => {
      if (!open || !contentRef.current) return;
      const el = contentRef.current;
      let mounted = true;

      const measure = () => {
        const originalWidth = el.style.width;
        el.style.width = 'auto';
        const naturalWidth = el.offsetWidth;
        el.style.width = originalWidth;

        const naturalHeight = el.offsetHeight;
        const MIN_HEIGHT = Math.max(300, Math.floor(window.innerHeight * 0.25)); // allow smaller minimum on small screens
        // Capped only enough to stay fully on-screen — the modal isn't manually
        // resizable, so this is the size it keeps for the rest of the session.
        const capWidth = Math.min(naturalWidth, MODAL_MAX_SIZE[0], window.innerWidth - MARGIN * 2);
        const capHeight = Math.min(naturalHeight, MODAL_MAX_SIZE[1], Math.max(MIN_HEIGHT, window.innerHeight - VIEWPORT_BUFFER));
        if (mounted) {
          setDimensions({ width: capWidth, height: capHeight });
          // compute initial contentMaxHeight so footer remains inside
          try {
            const headerH = headerRef.current ? headerRef.current.offsetHeight : 0;
            const footerH = footerRef.current ? footerRef.current.offsetHeight : 0;
            const buffer = 24;
            const calcMax = Math.max(120, capHeight - headerH - footerH - buffer);
            if (scrollRef.current) scrollRef.current.style.maxHeight = `${calcMax}px`;
          } catch (err) {
            void err;
          }
        }
      };

      const raf = window.requestAnimationFrame(measure);
      return () => {
        mounted = false;
        window.cancelAnimationFrame(raf);
      };
    }, [open]);

  // Reset position when the popup is closed so reopening starts fresh
  // (dimensions get re-measured from content the next time it opens).
  useEffect(() => {
    if (!open) {
      setDimensions({ width: 800, height: 870 });
      setPosition({ x: 0, y: 0 });
    }
  }, [open]);

  if (!container) return null;

  return createPortal(
    <Transition
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={open}
    >
      <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-auto">
        <Draggable
          handle=".resize-header"
          bounds="parent"
          position={position}
          onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
        >
          <div style={{ width: dimensions.width, height: dimensions.height }}>
            <div ref={contentRef} className="bg-white dialog pointer-events-auto border border-gray-300 rounded shadow-md h-full overflow-visible">
              <div className="" onClick={() => onCross()} aria-hidden="true" />
              <div className="flex flex-col flex-1 h-full min-h-0">
                <div className="relative flex-1 flex flex-col min-h-0">
                  <div
                    ref={headerRef}
                    className="dialog-header resize-header z-30 flex flex-row py-4 px-6 gap-4 items-start justify-between space-x-3 bg-gray-20 border-b border-gray-100 rounded-t"
                  >
                    <div className="flex flex-col space-x-1">
                      {selectedMedication !== null &&
                        selectedMedication.consumption !==
                        AdherenceTypes.AsPrescribed && (
                          <button
                            className="flex items-center"
                            onClick={() => {
                              moveBack();
                            }}
                          >
                            <ChevronLeftIcon className="w-4 h-4 text-teal-300 mb-2" />
                            <h3 className="flex flex-row text-body-sm font-semibold text-teal-300 hover:border-b-2 mb-2 hover:border-teal-300">
                              Back to Assessment
                            </h3>
                          </button>
                        )}
                      <h2 className="font-semibold text-teal-300 text-[22px] leading-8">
                        Create Intervention
                      </h2>
                    </div>
                    <div className="flex items-center h-7">
                      <button
                        type="button"
                        className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                        onClick={() => onClose(false)}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div ref={scrollRef} className="flex-1 pt-2 px-6 flex flex-col gap-2 overflow-auto pb-6 min-h-0">
                    {selectedMedication !== null && (
                      <div className="flex flex-col gap-12">
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col">
                            <div className="text-gray-800 flex flex-row text-xl px-4 py-2 mb-4 bg-blue-100 border-2 border-blue-400 rounded-md mt-2">
                              <span className="font-semibold text-body-lg text-REELTeal flex items-start">
                                Adherence Status -{" "}
                                {selectedMedication.consumption ===
                                  AdherenceTypes.Stopped
                                  ? "Stopped Taking Medication"
                                  : selectedMedication.consumption ===
                                    AdherenceTypes.NotAsPrescribed
                                    ? "Not Taking as Prescribed"
                                    : "Taking as Prescribed"}
                              </span>
                            </div>
                            <div className="flex-row text-gray-800 flex">
                              <span className="text-base font-medium items-start flex min-w-[290px]">
                                Original Prescription:
                              </span>
                              <p className="text-base font-semibold items-start flex justify-start">
                                {selectedMedication?.name}
                              </p>
                            </div>
                          </div>
                          {selectedMedication.consumption ===
                            AdherenceTypes.NotAsPrescribed && (
                              <div className="flex flex-row space-x-5">
                                <p className="flex-col min-w-[270px] flex items-start">
                                  <span className="text-base font-medium text-gray-800">
                                    Current Regimen
                                  </span>
                                </p>
                                <p className="min-w-[270px] flex flex-col items-start">
                                  <span className="text-base font-semibold text-REELTeal">
                                    {
                                      selectedMedication?.intervention
                                        ?.medicationName
                                    }{" "}
                                    {selectedMedication?.intervention?.route} [
                                    {selectedMedication?.intervention?.dosage}{" "}
                                    {selectedMedication?.intervention?.form}{" "}
                                    {selectedMedication?.intervention?.frequency}]
                                  </span>
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    <div className="gap-8">
                      <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-3 mt-2">
                          {selectedMedication === null && (
                            <>
                              <div className="flex w-full gap-3 overflow-hidden rounded  p-1 sm:gap-4">
                                <button
                                  type="button"
                                  className={`flex flex-1 flex-row items-center justify-center gap-3 rounded px-4 py-5 min-h-[90px] transition-colors sm:gap-4 ${
                                    interventionCategory ===
                                    interventionCategories.Medication
                                      ? "bg-teal-40 text-teal-300 border border-teal-300"
                                      : "bg-gray-10 text-gray-600 border border-gray-100 hover:border-REELTeal"
                                  }`}
                                  onClick={() =>
                                    setInterventionCategory(
                                      interventionCategories.Medication
                                    )
                                  }
                                >
                                  <img
                                    src={
                                      interventionCategory ===
                                      interventionCategories.Medication
                                        ? "/icons/intervention/forms/PharmaceuticalOpinionActive.svg"
                                        : "/icons/intervention/forms/PharmaceuticalOpinionInActive.svg"
                                    }
                                    alt=""
                                    className="h-[42px] w-[42px] shrink-0"
                                  />
                                  <span className="text-center text-lg font-semibold leading-7">
                                    Medication Intervention
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className={`flex flex-1 flex-row flex-wrap items-center justify-center gap-2 rounded px-4 py-5 min-h-[90px] transition-colors sm:gap-4 ${
                                    interventionCategory ===
                                    interventionCategories.Clinical
                                      ? "bg-teal-40 text-teal-300 border border-teal-300"
                                      : "bg-gray-10 text-gray-600 border border-gray-100 hover:border-REELTeal"
                                  }`}
                                  onClick={() => {
                                    setInterventionCategory(
                                      interventionCategories.Clinical
                                    );
                                    setLinkedPrescriptionId(null);
                                    setSelectedMedicine(null);
                                    setMedicationNameInputMode(false);
                                    setSelectedMedicineError(false);
                                  }}
                                >
                                  <img
                                    src={
                                      interventionCategory ===
                                      interventionCategories.Clinical
                                        ? "/icons/intervention/forms/GeneralInterventionActive.svg"
                                        : "/icons/intervention/forms/GeneralInterventionInActive.svg"
                                    }
                                    alt=""
                                    className="h-[42px] w-[42px] shrink-0"
                                  />
                                  <span className="text-center text-lg font-semibold leading-7">
                                    Clinical Intervention
                                  </span>
                                  <span
                                    className="inline-flex shrink-0 cursor-default"
                                    data-tooltip-id="clinical-intervention-segment-tooltip"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <ReactSVG
                                      src="/icons/information-dark.svg"
                                      className="max-w-4 w-4 text-gray-600"
                                    />
                                  </span>
                                </button>
                              </div>
                              <NewToolTip
                                id="clinical-intervention-segment-tooltip"
                                place="bottom"
                                render={() => (
                                  <div className="max-w-xs rounded bg-gray-10 p-3 text-sm text-gray-800 shadow">
                                   Create non-medication-related clinical interventions, including medical condition concerns, monitoring needs, and alternative care recommendations.
                                  </div>
                                )}
                              />

                              {interventionCategory ===
                                interventionCategories.Medication && (
                                <>
                                  <div className="flex w-full gap-3">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveMedicationEntry("link");
                                        setSelectedMedicine(null);
                                        setMedicationNameInputMode(false);
                                        setSelectedMedicineError(false);
                                        setMedicationType(null);
                                        setMedicationTypeError(false);
                                      }}
                                      className={`flex flex-1 items-center justify-center gap-2 rounded border px-4 py-3 text-base font-semibold transition-colors ${
                                        activeMedicationEntry === "link"
                                          ? "border-teal-300 bg-teal-40 text-teal-300"
                                          : "border-teal-300 bg-white text-teal-300 hover:bg-teal-25"
                                      }`}
                                    >
                                      <LinkIcon
                                        className="h-[18px] w-[18px] shrink-0"
                                        aria-hidden
                                      />
                                      Link to Current Prescription
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveMedicationEntry("add");
                                        setLinkedPrescriptionId(null);
                                      }}
                                      className={`flex flex-1 items-center justify-center gap-2 rounded border px-4 py-3 text-base font-semibold transition-colors ${
                                        activeMedicationEntry === "add"
                                          ? "border-teal-300 bg-teal-40 text-teal-300"
                                          : "border-teal-300 bg-white text-teal-300 hover:bg-teal-25"
                                      }`}
                                    >
                                      <span className="text-lg leading-none">+</span>
                                      Add New Medication
                                    </button>
                                  </div>

                                  {activeMedicationEntry === "link" && (
                                    <div className="w-full">
                                      {linkToCurrentPrescriptionOptions.length === 0 ? (
                                        <div className="rounded-[4px] border-[1px] border-gray-100 bg-gray-20 px-3 py-3 text-center">
                                          <p className="text-base font-semibold text-gray-800">
                                            No Prescriptions On File
                                          </p>
                                          <button
                                            type="button"
                                            className="text-sm font-medium text-teal-300 underline hover:text-REELTeal"
                                            onClick={() => {
                                              setActiveMedicationEntry("add");
                                              setLinkedPrescriptionId(null);
                                            }}
                                          >
                                            +Add a New Medication for this patient
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          <span className="flex items-center gap-1 mb-1 text-gray-900 text-body-xs">
                                            <LinkIcon
                                              className="h-4 w-4 shrink-0 text-gray-700"
                                              aria-hidden
                                            />
                                            Choose Prescription to Link *
                                          </span>
                                          <div className="relative w-full">
                                            <Listbox
                                              value={selectedLinkPrescriptionOption}
                                              onChange={(option) => {
                                                if (!option) return;
                                                const nextId =
                                                  option.value != null
                                                    ? option.value
                                                    : null;
                                                setLinkedPrescriptionId(nextId);
                                                if (nextId) {
                                                  setSelectedMedicine(null);
                                                  setMedicationNameInputMode(false);
                                                  setSelectedMedicineError(false);
                                                }
                                              }}
                                            >
                                              {({ open }) => (
                                                <>
                                                  <Listbox.Button
                                                    type="button"
                                                    className="flex min-h-10 w-full items-center justify-between rounded-[4px] border-[1px] border-gray-200 bg-white px-3 py-3 text-left outline-none ring-0 focus:outline-none focus-visible:border-[2px] focus-visible:border-REELTeal"
                                                  >
                                                    <span
                                                      className={`min-w-0 flex-1 truncate text-base font-normal leading-normal ${
                                                        selectedLinkPrescriptionOption
                                                          ? "text-gray-800"
                                                          : "text-gray-700"
                                                      }`}
                                                    >
                                                      {selectedLinkPrescriptionOption?.label ??
                                                        "Select prescription on File"}
                                                    </span>
                                                    <div className="ml-2 flex shrink-0 items-center gap-2">
                                                      {selectedLinkPrescriptionOption && (
                                                        <>
                                                          <ReactSVG
                                                            src="/icons/input-cross.svg"
                                                            className="h-3 w-3 cursor-pointer text-gray-800 hover:text-gray-700"
                                                            onClick={(e) => {
                                                              e.preventDefault();
                                                              e.stopPropagation();
                                                              setLinkedPrescriptionId(
                                                                null
                                                              );
                                                            }}
                                                          />
                                                          <div className="h-5 w-px bg-gray-200" />
                                                        </>
                                                      )}
                                                      <ChevronDownIcon
                                                        className={`h-5 w-5 shrink-0 text-gray-800 transition-transform ${
                                                          open
                                                            ? "-rotate-180"
                                                            : ""
                                                        }`}
                                                        aria-hidden
                                                      />
                                                    </div>
                                                  </Listbox.Button>
                                                  <Listbox.Options className="absolute left-0 z-50 mt-1 max-h-72 w-full overflow-y-auto overscroll-contain rounded-t-none bg-white shadow-lg">
                                                    {prescriptionLinkGroups.map(
                                                      (group) => (
                                                        <div
                                                          key={group.heading}
                                                          className="border-b border-gray-100 last:border-b-0"
                                                        >
                                                          <div
                                                            className="sticky top-0 z-10 bg-gray-10 px-3 py-2 text-base font-semibold text-gray-700"
                                                            aria-hidden
                                                          >
                                                            {group.heading}
                                                          </div>
                                                          {group.items.map(
                                                            (option, index) => (
                                                              <Listbox.Option
                                                                key={
                                                                  option.value ??
                                                                  `${group.heading}-${index}`
                                                                }
                                                                value={option}
                                                                disabled={
                                                                  option.disabled
                                                                }
                                                              >
                                                                {({
                                                                  active,
                                                                  selected,
                                                                }) => (
                                                                  <div
                                                                    className={`flex flex-col justify-between overflow-hidden px-3 py-2.5 pl-4 text-sm whitespace-pre ${
                                                                      option.disabled
                                                                        ? "cursor-not-allowed text-gray-500"
                                                                        : selected
                                                                          ? "cursor-pointer bg-teal-100 text-white hover:bg-teal-100 hover:text-white"
                                                                          : "cursor-pointer text-gray-800 hover:bg-blue-100"
                                                                    } ${active ? "" : ""}`}
                                                                  >
                                                                    {option.label}
                                                                  </div>
                                                                )}
                                                              </Listbox.Option>
                                                            )
                                                          )}
                                                        </div>
                                                      )
                                                    )}
                                                  </Listbox.Options>
                                                </>
                                              )}
                                            </Listbox>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}

                                  {activeMedicationEntry === "add" && (
                                    <div className="w-full">
                                      <MedicineSelect
                                        onChange={(medicine) => {
                                          setLinkedPrescriptionId(null);
                                          setSelectedMedicine(medicine);
                                        }}
                                        isRequired={true}
                                        onNewCreate={(value) =>
                                          setMedicationNameInputMode(value)
                                        }
                                        error={selectedMedicineError}
                                        value={selectedMedicine}
                                        topSlot={
                                          <CustomInput
                                            name="medicationType"
                                            label="Medication Type"
                                            type="dropdown"
                                            options={medicationTypeOptions}
                                            value={medicationType}
                                            onChange={(option) => {
                                              setMedicationType(option?.value ?? null);
                                              setMedicationTypeError(false);
                                            }}
                                            required
                                            error={medicationTypeError}
                                            className="col-span-12 h-full"
                                          />
                                        }
                                        type="Medication"
                                      />
                                    </div>
                                  )}
                                  {medicationNameInputMode && (
                                    <p className="col-span-12 text-base text-red-200 font-medium">
                                      This is an UNCODED DRUG - No Clinical Check will
                                      be performed
                                    </p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>

                        <div className="border-t-[1px] border-gray-100"></div>
                        <div className="flex flex-col gap-1">
                        <div className="flex flex-row space-x-2">
                          <p className="min-w-[270px] text-gray-800 flex items-center">
                            <span className="text-base font-normal">
                              Intervention Type *
                            </span>
                          </p>
                          <div className="flex gap-10">
                            <div className="flex items-center gap-2">
                              <input
                                id="patient"
                                name="Attendee-type"
                                type="radio"
                                value={interventionTypes.Pharmaceutical}
                                checked={
                                  interventionType ===
                                  interventionTypes.Pharmaceutical
                                }
                                className="w-4 h-4  cursor-pointer  text-teal-50  focus:ring-transparent"
                                onChange={() => {
                                  setInterventionType(
                                    interventionTypes.Pharmaceutical
                                  );
                                }}
                              />
                              <label
                                className="cursor-pointer text-base font-normal whitespace-nowrap"
                                htmlFor="patient"
                              >
                                Pharmaceutical Opinion
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                id="patient-caregiver"
                                name="Attendee-type"
                                type="radio"
                                value={interventionTypes.Prescription}
                                checked={
                                  interventionType ===
                                  interventionTypes.Prescription
                                }
                                disabled
                                className="w-4 h-4 cursor-not-allowed text-teal-50 opacity-40 focus:ring-transparent"
                                onChange={() => {}}
                              />
                              <label
                                className="cursor-not-allowed text-base font-normal text-gray-400 whitespace-nowrap"
                                htmlFor="patient-caregiver"
                              >
                                Prescription Adaptation
                              </label>
                            </div>
                          </div>
                        </div>
                        {interventionTypeError && (
                          <p className="pl-[270px] text-sm font-medium text-red-200">
                            Intervention type is required.
                          </p>
                        )}
                        </div>
                        <div className="flex flex-row space-x-2">
                          <p className="min-w-[270px] text-gray-800 flex items-center">
                            <span className="text-base font-normal">
                              {interventionType === interventionTypes.Pharmaceutical
                                ? "Drug-Therapy Problem (DTP) *"
                                : "Select Reason for Adaptation *"}
                            </span>
                          </p>
                          <CustomInput
                            options={
                              interventionType === interventionTypes.Pharmaceutical
                                ? DPTOptions
                                : AdaptationOptions
                            }
                            name={
                              interventionType === interventionTypes.Pharmaceutical
                                ? "DPTOptions"
                                : "AdaptationOptions"
                            }
                            className="w-full bg-white !text-sm !font-normal"
                            label=""
                            type="dropdown"
                            value={DTPValue}
                            onChange={(e) => setDTPValue(e.value)}
                            inputClassName="!text-sm"
                            error={DTPError}
                          />
                        </div>
                        <div className="flex flex-row space-x-2">
                          <p className="flex-col min-w-[270px] flex items-start">
                            <span className="text-base font-normal">
                              Action Plan (Optional)
                              <br />
                              (Select all that apply)
                            </span>
                          </p>
                          <div className="grid grid-cols-1 gap-1 min-w-[480px]">
                            {actionPlanOptions.map((item) => {
                              const fieldId = actionPlanFieldId(item.label);
                              return (
                              <span
                                key={item.label}
                                className="flex items-center text-body-md font-normal py-0"
                              >
                                <input
                                  className=""
                                  type="checkbox"
                                  id={fieldId}
                                  name={item.label}
                                  checked={actionPlan?.includes(item.label)}
                                  onChange={handleChange}
                                />
                                <label
                                  htmlFor={fieldId}
                                  className="ml-4 text-gray-800 text-base font-normal"
                                >
                                  {item.label}
                                </label>
                              </span>
                              );
                            })}
                            <span className="flex items-center">
                              <input
                                className=""
                                type="checkbox"
                                checked={other}
                                onChange={(e) => {
                                  setOther(e.target.checked);
                                  if (e.target.checked) {
                                    setActionPlan(
                                      actionPlan.filter(
                                        (item) =>
                                          item !==
                                          DOES_NOT_APPLY_ACTION_PLAN_VALUE
                                      )
                                    );
                                  }
                                }}
                              />
                              <label className="ml-4 text-gray-800 text-base font-normal">
                                Other (specify)
                              </label>
                            </span>
                            {other && (
                              <CustomInput
                                name="SpecifyOther"
                                className="col-span-1 w-full"
                                label=""
                                placeholder="Specify other"
                                type="text"
                                inputClassName="!text-base"
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-row space-x-2">
                          <p className="min-w-[270px] text-gray-800 flex items-center">
                            <span className="text-base font-normal">
                              Urgency Level *
                            </span>
                          </p>
                          <div className="col-span-12 flex flex-row items-start">
                            {URGENCY_LEVELS.map(({ value, label }) => (
                              <button
                                key={value}
                                type="button"
                                className={`${PILL_TOGGLE_CLASS} ${
                                  urgency === value ? "bg-gray-20" : "bg-white"
                                }`}
                                onClick={() => setUrgency(value)}
                              >
                                <h3 className="text-body-md font-semibold text-teal-300">
                                  {label}
                                </h3>
                              </button>
                            ))}
                          </div>
                          </div>
                          {urgencyError && (
                            <p className="pl-[270px] text-sm font-medium text-red-200">
                              Urgency level is required.
                            </p>
                          )}
                        </div>
                        {interventionType === interventionTypes.Pharmaceutical && (
                          <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start sm:gap-10">
                              <div className="flex min-w-0 flex-col gap-1">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="text-base font-semibold text-gray-800">
                                    Link to a Medication Review
                                  </span>
                                  <span
                                    className="inline-flex shrink-0 cursor-default"
                                    data-tooltip-id="link-mr-pmr-tooltip"
                                  >
                                    <ReactSVG
                                      src="/icons/information-dark.svg"
                                      className="max-w-4 w-4 text-gray-600"
                                    />
                                  </span>
                                  <NewToolTip
                                    id="link-mr-pmr-tooltip"
                                    place="bottom"
                                    render={() => (
                                      <div className="max-w-sm rounded bg-gray-10 p-3 text-sm text-gray-800 shadow">
                                        <p className="mb-2">
                                          A linked medication review is optional. If applicable, select the current-year medication review or follow-up.
                                        </p>
                                      </div>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="min-w-0 sm:max-w-md">
                              {personalMrLinkOptions.length === 0 ? (
                                <div className="rounded border border-gray-100 bg-gray-10 px-3 py-2 text-sm leading-snug text-gray-600">
                                  No current-year medication reviews/ follow-ups found.
                                </div>
                              ) : (
                                <CustomInput
                                  name="linkedMedicationReviewClinicalService"
                                  className="w-full !border-gray-100 !py-2 !text-sm"
                                  label=""
                                  type="dropdown"
                                  options={personalMrLinkOptions}
                                  value={linkedMedicationReviewClinicalServiceId}
                                  selectedDisplayLabel={
                                    linkedMedicationReviewClinicalServiceId
                                      ? undefined
                                      : "Select a medication review"
                                  }
                                  placeholder=""
                                  onChange={(e) => {
                                    const next = e?.value;
                                    setLinkedMedicationReviewClinicalServiceId(
                                      next != null && next !== ""
                                        ? next
                                        : null
                                    );
                                  }}
                                  inputClassName="!text-sm"
                                />
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex flex-col text-base font-normal">
                          <CustomInput
                            name="comments"
                            className="col-span-12 "
                            inputClassName="!placeholder-gray-600 min-h-[40px] max-h-[96px] resize-y whitespace-pre-wrap break-words"
                            label=""
                            placeholder="Intervention Comments (Optional)"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            type="textarea"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div ref={footerRef} className="bg-gray-20 z-30 py-4 border-t border-gray-100 flex-none">
                    <div className="px-6">
                      <div className="w-full flex flex-wrap items-center gap-4">
                        <Button
                          className="flex-1 min-w-[120px] max-w-[340px]"
                          text="Cancel"
                          type="primary-outline"
                          disabled={isSaving}
                          onClick={() => {
                            onClose(false);
                          }}
                        />
                        <Button
                          className="flex-1 min-w-[120px] max-w-[340px]"
                          text="Complete Now"
                          type="primary-outline"
                          toolTipId="complete-now-tooltip"
                          TooltipComponent={() => (
                            <div className="bg-white p-2 flex flex-col max-w-[300px]">
                              <p className="text-gray-800 text-sm font-normal">
                                Complete Interventions by selecting the
                                Interventions entry from the main menu.
                              </p>
                            </div>
                          )}
                          disabled={
                            urgency === null ||
                            DTPValue === null ||
                            isSaving
                          }
                          onClick={handleCompleteNow}
                        />
                        <Button
                          className="flex-none w-[min(100%,15.5rem)] sm:w-[min(100%,17rem)] !px-3 py-3 text-base leading-snug whitespace-normal"
                          text={
                            selectedMedication?.intervention?.urgencyLevel !==
                              null &&
                              selectedMedication?.intervention?.urgencyLevel !==
                              undefined
                              ? "Update & Complete Later"
                              : "Save & Complete Later"
                          }
                          type="primary"
                          disabled={
                            urgency === null ||
                            DTPValue === null ||
                            isSaving
                          }
                          onClick={() => {
                            if (medicationNameInputMode) {
                              setOpenUncodedDrug(true);
                            } else manageStep();
                          }}
                          iconUrl="/icons/information-light.svg"
                          toolTipId="btn-tooltip"
                          TooltipComponent={InterventionCreateNote}
                        />
                      </div>
                    </div>
                  </div>
                  <ConfirmationModalNew
                    open={openUncodedDrug}
                    title={"Uncoded Drug Alert"}
                    type={"primary"}
                    confirmText={"Continue"}
                    cancelText="Cancel & Review"
                    content={() => (
                      <div className="flex flex-col  text-gray-700">
                        <p className="text-body-sm ">
                          An uncoded DRUG has been added.
                          <br />
                          Profile Analysis can not be performed.
                        </p>
                        <p className="text-body-sm mt-4">
                          A manual clinical check will be required.
                        </p>
                      </div>
                    )}
                    onCross={() => {
                      setOpenUncodedDrug(false);
                    }}
                    handleClose={() => {
                      setOpenUncodedDrug(false);
                    }}
                    confirmAction={() => {
                      setOpenUncodedDrug(false);
                      manageStep();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Draggable>
      </div>
    </Transition>,
    container
  );
};

export default InterventionCreate;